/**
 * SPEC-PreScreening-Phase1-UI (WI-6 + WI-7) — prescreen decision logic.
 *
 * Single source of truth for the wiring-critical decisions in the prescreen
 * surfaces. The page components (`pages/prescreen-list.tsx`,
 * `pages/qa-detail-v3.tsx`) import these so the behavior the UI ships is the
 * behavior `lib/__tests__/prescreen-helpers.test.ts` locks. Do NOT inline copies
 * of these rules in the components — that re-introduces the mocked-prop drift
 * this module exists to prevent.
 */

import { DROP_ZONE_CODES, type DropZoneCode } from "@shared/schema";

/**
 * Literal rendered in every "Actual" cell while a delivery is pre-delivery.
 * Em-dash (U+2014), not a hyphen — locked by the acceptance checklist
 * ("every actual reads 'N/A — not yet delivered'"). Not a blank em-dash,
 * not an error string.
 */
export const PRESCREEN_ACTUAL_NA = "N/A — not yet delivered" as const;

/** Empty-state copy for the prescreen surfaces (kept here so tests can lock them). */
export const PRESCREEN_NO_DEST_NOTES = "No destination notes." as const;
export const PRESCREEN_NO_PDR_HISTORY = "No verified history for this address" as const;
export const DZE_PLACEHOLDER = "Select code…" as const;

/**
 * WI-7 mode detection. `onfleetState === 1` (assigned, pre-delivery) is the
 * authoritative signal; a set `prescreenStatus` is a secondary marker that
 * survives the Phase-0 merge across re-import/completion. `?prescreen=1` in the
 * URL is only a hint (handled at the call site) — the row's state is the truth.
 */
export function isPrescreen(
  delivery: { onfleetState?: number | null; prescreenStatus?: string | null } | null | undefined,
): boolean {
  if (!delivery) return false;
  return delivery.onfleetState === 1 || delivery.prescreenStatus != null;
}

/**
 * Human-readable labels for the constrained DZ <Select>. Keyed by the canonical
 * DROP_ZONE_CODES so the option list can never drift from the source of truth.
 */
export const DZE_OPTION_LABELS: Record<DropZoneCode, string> = {
  DOOR: "Front door / unit door",
  OTHER_ENTRANCE: "Other entrance (side, rear, garage)",
  INTERIOR_PORCH: "Interior porch / vestibule",
  FRONT_DESK: "Front desk / concierge",
  STORAGE: "Storage / mailroom / locker",
  H2H: "Hand to hand",
  UNK: "Undetermined",
  UNSAFE: "Unsafe — exposed public area",
};

const DROP_ZONE_CODE_SET = new Set<string>(DROP_ZONE_CODES);

/**
 * Resolve the value the DZ <Select> should be bound to. Returns the canonical
 * code only when it is genuinely on the approved list; otherwise returns "" so
 * the trigger shows the "Select code…" placeholder. Critically: a null/empty or
 * off-list value is NEVER coerced to DOOR — the reviewer must choose deliberately.
 */
export function resolveDzeSelectValue(code: string | null | undefined): string {
  if (!code) return "";
  return DROP_ZONE_CODE_SET.has(code) ? code : "";
}

/**
 * Footer branch selector. Prescreen takes priority over every other state
 * (there is no POD to judge pre-delivery, so Good/Bad/Unclear/Resolve and the
 * review-mode verdict controls must not render). Below prescreen, the existing
 * review → completed → decision priority is preserved unchanged.
 */
export function footerMode(opts: {
  isPrescreen: boolean;
  isReviewMode: boolean;
  isCompleted: boolean;
  isEditing: boolean;
}): "prescreen" | "review" | "completed" | "decision" {
  if (opts.isPrescreen) return "prescreen";
  if (opts.isReviewMode) return "review";
  if (opts.isCompleted && !opts.isEditing) return "completed";
  return "decision";
}

export type PrescreenQueue = "waiting" | "in_progress" | "completed";
const VALID_QUEUES: readonly PrescreenQueue[] = ["waiting", "in_progress", "completed"];

/** Read the active queue from the URL search string; default "waiting". */
export function parseQueueParam(search: string): PrescreenQueue {
  const q = new URLSearchParams(search).get("queue");
  return (VALID_QUEUES as readonly string[]).includes(q ?? "")
    ? (q as PrescreenQueue)
    : "waiting";
}

/** Build the canonical `?date=…&queue=…` search string for deep-linking a queue. */
export function buildQueueSearch(queue: PrescreenQueue, date: string | null): string {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  params.set("queue", queue);
  return `?${params.toString()}`;
}

export interface PrescreenListResponse {
  waiting?: unknown[];
  inProgress?: unknown[];
  completed?: unknown[];
  // Deployed WI-2 emits counts: { waiting, inProgress, completed, total } —
  // it does NOT send prescreened/remaining, so those are derived here.
  counts?: {
    waiting?: number;
    inProgress?: number;
    completed?: number;
    total?: number;
  };
}

/**
 * Stat-tile counts (Total / Prescreened / Remaining). Reconciled with the
 * deployed WI-2 contract, which returns counts:{waiting,inProgress,completed,total}
 * (no prescreened/remaining). We take `total` from the server when present and
 * DERIVE prescreened (= completed) and remaining (= total − prescreened) so the
 * Prescreened/Remaining tiles are never blank. Falls back to summing the three
 * flat groups when no counts block is present.
 */
export function groupedQueueCounts(
  resp: PrescreenListResponse | null | undefined,
): { total: number; prescreened: number; remaining: number } {
  const waiting = resp?.waiting?.length ?? 0;
  const inProgress = resp?.inProgress?.length ?? 0;
  const completed = resp?.completed?.length ?? 0;
  const total = resp?.counts?.total ?? waiting + inProgress + completed;
  const prescreened = resp?.counts?.completed ?? completed;
  return { total, prescreened, remaining: total - prescreened };
}

/**
 * Map an EditableCell/Select fieldKey to the RAW expected* column the deployed
 * WI-5 endpoint (`PATCH /api/qa/prescreening/:id/expected`) accepts. That handler
 * only persists `expectedStreetNumber`, `expectedUnitNumber`, `expectedDzeCode`;
 * `recipient` is the lookup key (P1 alias path, out of scope) and intentionally
 * has no mapping, so it is never PATCHed (avoids a 400). Returns null for any
 * field the endpoint won't accept.
 */
const PRESCREEN_EXPECTED_COLUMN: Record<string, string> = {
  streetNumber: "expectedStreetNumber",
  unitNumber: "expectedUnitNumber",
  dzeCode: "expectedDzeCode",
};

export function toExpectedColumn(fieldKey: string): string | null {
  return PRESCREEN_EXPECTED_COLUMN[fieldKey] ?? null;
}

export type ReferencePillState = "verified" | "first_time";

/**
 * WI-6 Reference column. Per spec: `pdrVerifiedCount != null` → verified pill
 * (a PDR record exists, even a freshly seeded one with count 0); else the row
 * is a first-time address.
 */
export function referencePillState(pdrVerifiedCount: number | null | undefined): ReferencePillState {
  return pdrVerifiedCount != null ? "verified" : "first_time";
}

/** EST calendar date as YYYY-MM-DD (mirrors qa-list.tsx getESTDate). */
export function getESTDate(date: Date = new Date()): string {
  return date.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

/**
 * Given a YYYY-MM-DD date, return the upcoming Sunday (the same day if it is
 * already Sunday). Computed at noon UTC to stay clear of DST boundaries.
 */
export function nextSunday(fromYmd: string): string {
  const [y, m, d] = fromYmd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const dow = dt.getUTCDay(); // 0 = Sunday
  const add = dow === 0 ? 0 : 7 - dow;
  dt.setUTCDate(dt.getUTCDate() + add);
  return dt.toISOString().slice(0, 10);
}

/** Convenience: the next Sunday relative to "today" in EST — the date-picker default. */
export function getNextSundayEST(now: Date = new Date()): string {
  return nextSunday(getESTDate(now));
}

// ─────────────────────────────────────────────────────────────────────────────
// SPEC-PreScreening Phase 3 (WI-P3-3) — bulk-pull SSE-client wiring logic.
//
// No jsdom is configured in this repo (component rendering is covered by the
// Replit dev-server smoke test), so the wiring-critical bits that are easy to get
// wrong live here and are locked by lib/__tests__/prescreen-pull.test.ts:
// the stream URL, the live button label, the done-toast copy (incl. the
// "N tasks need manual DZ" line), and SSE error-reason parsing. prescreen-list.tsx
// imports these — it never re-implements them.
// ─────────────────────────────────────────────────────────────────────────────

export interface PullProgress {
  processed: number;
  total: number;
  errors: number;
}

/** WI-P3-2 SSE endpoint URL for a delivery date. */
export function buildBulkStreamUrl(date: string): string {
  return `/api/qa/prescreening/bulk-fetch-stream?date=${encodeURIComponent(date)}`;
}

/**
 * Button label. While pulling with a progress frame, show a thousands-separated
 * live counter ("Loading 100 / 1,712…"); otherwise (idle, or pulling before the
 * first progress event) show the default "Pull {date}'s QATT_1".
 */
export function formatPullLabel(opts: {
  isPulling: boolean;
  pullProgress: PullProgress | null;
  selectedDate: string;
}): string {
  if (opts.isPulling && opts.pullProgress) {
    return `Loading ${opts.pullProgress.processed.toLocaleString()} / ${opts.pullProgress.total.toLocaleString()}…`;
  }
  return `Pull ${opts.selectedDate}'s QATT_1`;
}

export interface PullDoneData {
  imported?: number;
  errors?: Array<{ taskId: string; reason: string }>;
}

/**
 * Success-toast copy from the `done` summary. Title reports the imported count
 * (thousands-separated); when some tasks errored (BERT → null DZ), add a
 * "N task(s) need(s) manual DZ selection." description.
 */
export function buildPullDoneToast(
  data: PullDoneData,
  selectedDate: string,
): { title: string; description?: string } {
  const imported = data?.imported ?? 0;
  const title = `Pulled ${imported.toLocaleString()} tasks for ${selectedDate}.`;
  const errCount = data?.errors?.length ?? 0;
  if (errCount <= 0) return { title };
  const noun = errCount === 1 ? "task needs" : "tasks need";
  return { title, description: `${errCount.toLocaleString()} ${noun} manual DZ selection.` };
}

/**
 * Extract the failure reason from an SSE `error` event's JSON data. The browser
 * also fires a native EventSource `error` (a plain Event with no parseable data)
 * for connection drops — fall back to a generic message for those.
 */
export function parsePullErrorReason(raw: unknown): string {
  if (typeof raw === "string" && raw.length > 0) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.reason === "string" && parsed.reason.length > 0) {
        return parsed.reason;
      }
    } catch {
      /* not JSON — fall through to the generic message */
    }
  }
  return "Connection error — check server logs.";
}
